import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { NavItem, NavSubItem } from '../src/utils/navigationConfig';

interface SidebarNavItemProps {
  item: NavItem;
  isActive: boolean;
  isSubItemActive: boolean;
  activeSubItemId?: string;
  onNavigate: (id: string) => void;
  isCollapsed: boolean;
}

export function SidebarNavItem({
  item,
  isActive,
  isSubItemActive,
  activeSubItemId,
  onNavigate,
  isCollapsed,
}: SidebarNavItemProps) {
  const [isExpanded, setIsExpanded] = useState(isActive || isSubItemActive);
  const Icon = item.icon;
  const hasSubItems = item.subItems && item.subItems.length > 0;

  React.useEffect(() => {
    if (isActive || isSubItemActive) {
      setIsExpanded(true);
    }
  }, [isActive, isSubItemActive]);

  // Collapse submenus when sidebar is collapsed
  React.useEffect(() => {
    if (isCollapsed) {
      setIsExpanded(false);
    }
  }, [isCollapsed]);

  const handleMainClick = () => {
    // When collapsed, don't toggle submenus, just navigate
    if (isCollapsed) {
      onNavigate(item.id);
      return;
    }

    if (hasSubItems) {
      setIsExpanded(!isExpanded);
      // If clicking on the parent and it has subitems, also navigate to it
      if (!isExpanded) {
        onNavigate(item.id);
      }
    } else {
      onNavigate(item.id);
    }
  };

  const handleSubItemClick = (subItem: NavSubItem) => {
    onNavigate(subItem.id);
  };

  return (
    <div>
      {/* Main nav item */}
      <button
        onClick={handleMainClick}
        className={`
          w-full flex items-center gap-3 py-3 px-4
          transition-colors duration-200 relative
          ${isCollapsed ? 'justify-center' : 'justify-between'}
          ${
            isActive && !isSubItemActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-l-sidebar-primary shadow-sm'
              : 'text-muted-foreground hover:bg-sidebar-accent/65 hover:text-foreground'
          }
        `}
        title={isCollapsed ? item.label : undefined}
      >
        <div className={`flex items-center gap-3 ${isCollapsed ? '' : 'flex-1'}`}>
          <Icon className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>}
        </div>

        {!isCollapsed && (
          <div className="flex items-center gap-2">
            {item.badge !== undefined && item.badge > 0 && (
              <div className="bg-red-500 text-white text-xs rounded-full min-w-5 h-5 flex items-center justify-center px-1.5">
                {item.badge > 99 ? '99+' : item.badge}
              </div>
            )}
            {hasSubItems && (
              <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
            )}
          </div>
        )}

        {/* Badge indicator when collapsed */}
        {isCollapsed && item.badge !== undefined && item.badge > 0 && (
          <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {/* Submenu items - only show when not collapsed */}
      <AnimatePresence>
        {hasSubItems && isExpanded && !isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="mx-2 mb-1 overflow-hidden rounded-lg border border-sidebar-border/60 bg-sidebar-accent/35"
          >
            {item
              .subItems!.filter((subItem) => !subItem.hidden)
              .map((subItem) => {
                const SubIcon = subItem.icon;
                return (
                  <button
                    key={subItem.id}
                    onClick={() => handleSubItemClick(subItem)}
                    className={`
                    w-full flex items-center gap-3 py-2 px-6 pl-12
                    transition-colors duration-200 text-sm
                    ${
                      activeSubItemId === subItem.id
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-l-sidebar-primary'
                        : 'text-muted-foreground hover:bg-sidebar-accent/70 hover:text-foreground'
                    }
                  `}
                  >
                    {SubIcon ? (
                      <SubIcon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
                    ) : (
                      <span className="w-4 h-4 flex-shrink-0 rounded-full bg-current opacity-40" aria-hidden />
                    )}
                    {subItem.label}
                  </button>
                );
              })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
